import { ProductType } from 'types/Products';
import {
  CertificationOptions,
  OriginOptions
} from '../../../../api/types/ReferenceData';
import { UxBeautyPrivateAttributes } from 'types/Events';
import { YoutubeVideoField } from '../editor/fields/common/YoutubeVideoField';
import { TaglineField } from '../editor/fields/common/TaglineField';
import { AwardsField } from '../editor/fields/common/AwardsField';
import { MarketTrendsField } from '../editor/fields/common/MarketTrendsField';
import { SegmentTag } from 'types/global';
import { TechnologyField } from '../editor/fields/common/TechnologyField';
import { PreservativeField } from '../editor/fields/common/PreservativeField';
import { MaterialCodeField } from '../editor/fields/common/MaterialCodeField';
import { CategoryManagerField } from '../editor/fields/common/CategoryManagerField';
import { CountryOriginField } from '../editor/fields/common/CountryOriginField';
import { ProductionSiteField } from '../editor/fields/common/ProductionSiteField';
import { ClinicalTimeDeliveryField } from '../editor/fields/common/ClinicalTimeDeliveryField';
import { StrategicPortfolioField } from '../editor/fields/common/StrategicPortfolioField';
import { PositioningField } from '../editor/fields/common/PositioningField';
import { SampleUnitPriceField } from '../editor/fields/common/SampleUnitPriceField';
import { SampleUnitSizeField } from '../editor/fields/common/SampleUnitSizeField';
import { StandardLeadTimeField } from '../editor/fields/common/StandardLeadTimeField';
import { StorageConditionsField } from '../editor/fields/common/StorageConditionsField';
import { ExtraRndTestsField } from '../editor/fields/common/ExtraRndTestsField';
import { BusinessPriorityField } from '../editor/fields/common/BusinessPriorityField';
import { PhStabilityRangeField } from '../editor/fields/common/PhStabilityRangeField';
import { TemperatureStabilityField } from '../editor/fields/common/TemperatureStabilityField';
import { FormulationTipsField } from '../editor/fields/common/FormulationTipsField';
import { BiologicalTargetField } from '../editor/fields/common/BioLogicalTarget';
import { InStockField } from '../editor/fields/common/InStockField';
import { ConsumerTestsField } from '../editor/fields/common/ConsumerTestsField';
import { ViscosityField } from '../editor/fields/common/ViscosityField';
import { LifeCycleAnalysisField } from '../editor/fields/common/LifeCycleAnalysisField';

export const isProductOutOfStock = (inStock: string | undefined) =>
  inStock === 'false' ? true : false;
export const getUxbPrivateAttributes = (
  productType: ProductType
): {
  key: UxBeautyPrivateAttributes;
  type: 'string' | 'singleSelect' | 'multiSelect' | 'boolean';
  isPrivate: boolean;
  element: JSX.Element;
}[] => {
  switch (productType) {
    case ProductType.INGREDIENTS:
      return [
        {
          key: UxBeautyPrivateAttributes.VIDEO,
          type: 'string',
          isPrivate: false,
          element: <YoutubeVideoField />
        },
        {
          key: UxBeautyPrivateAttributes.TAGLINE,
          type: 'string',
          isPrivate: false,
          element: <TaglineField />
        },
        {
          key: UxBeautyPrivateAttributes.AWARDS,
          type: 'multiSelect',
          isPrivate: false,
          element: <AwardsField />
        },
        {
          key: UxBeautyPrivateAttributes.MARKET_TRENDS,
          type: 'multiSelect',
          isPrivate: true,
          element: <MarketTrendsField segment={SegmentTag.PERSONALCARE} />
        },
        {
          key: UxBeautyPrivateAttributes.TECHNOLOGY,
          type: 'multiSelect',
          isPrivate: false,
          element: <TechnologyField />
        },
        {
          key: UxBeautyPrivateAttributes.PRESERVATIVE,
          type: 'singleSelect',
          isPrivate: true,
          element: <PreservativeField />
        },
        {
          key: UxBeautyPrivateAttributes.MATERIAL_CODE,
          type: 'string',
          isPrivate: true,
          element: <MaterialCodeField />
        },
        {
          key: UxBeautyPrivateAttributes.CATEGORY_MANAGER,
          type: 'string',
          isPrivate: true,
          element: <CategoryManagerField />
        },
        {
          key: UxBeautyPrivateAttributes.COUNTRY_ORIGIN,
          type: 'multiSelect',
          isPrivate: true,
          element: <CountryOriginField />
        },
        {
          key: UxBeautyPrivateAttributes.PRODUCTION_SITE,
          type: 'singleSelect',
          isPrivate: true,
          element: <ProductionSiteField segment={SegmentTag.PERSONALCARE} />
        },
        {
          key: UxBeautyPrivateAttributes.CLINICAL_TIME_DELIVERY,
          type: 'multiSelect',
          isPrivate: true,
          element: <ClinicalTimeDeliveryField />
        },
        {
          key: UxBeautyPrivateAttributes.STRATEGIC_PORTFOLIO,
          type: 'boolean',
          isPrivate: true,
          element: <StrategicPortfolioField />
        },
        {
          key: UxBeautyPrivateAttributes.POSITIONING,
          type: 'singleSelect',
          isPrivate: true,
          element: <PositioningField segment={SegmentTag.PERSONALCARE} />
        },
        {
          key: UxBeautyPrivateAttributes.LIFE_CYCLE_ANALYSIS,
          type: 'boolean',
          isPrivate: true,
          element: <LifeCycleAnalysisField />
        },
        {
          key: UxBeautyPrivateAttributes.SAMPLE_UNIT_PRICE,
          type: 'string',
          isPrivate: true,
          element: <SampleUnitPriceField />
        },
        {
          key: UxBeautyPrivateAttributes.SAMPLE_UNIT_SIZE,
          type: 'string',
          isPrivate: true,
          element: <SampleUnitSizeField />
        },
        {
          key: UxBeautyPrivateAttributes.STANDARD_LEAD_TIME,
          type: 'string',
          isPrivate: true,
          element: <StandardLeadTimeField />
        },
        {
          key: UxBeautyPrivateAttributes.STORAGE_CONDITIONS,
          type: 'string',
          isPrivate: true,
          element: <StorageConditionsField />
        },
        {
          key: UxBeautyPrivateAttributes.EXTRA_RND_TESTS,
          type: 'boolean',
          isPrivate: true,
          element: <ExtraRndTestsField />
        },
        {
          key: UxBeautyPrivateAttributes.BUSINESS_PRIORITY,
          type: 'singleSelect',
          isPrivate: true,
          element: <BusinessPriorityField />
        },
        {
          key: UxBeautyPrivateAttributes.PH_STABILITY_RANGE,
          type: 'string',
          isPrivate: true,
          element: <PhStabilityRangeField />
        },
        {
          key: UxBeautyPrivateAttributes.TEMPERATURE_STABILITY,
          type: 'string',
          isPrivate: true,
          element: <TemperatureStabilityField />
        },
        {
          key: UxBeautyPrivateAttributes.FORMULATION_TIPS,
          type: 'string',
          isPrivate: true,
          element: <FormulationTipsField />
        },
        {
          key: UxBeautyPrivateAttributes.BIOLOGICAL_TARGET,
          type: 'multiSelect',
          isPrivate: true,
          element: <BiologicalTargetField />
        },
        {
          key: UxBeautyPrivateAttributes.IN_STOCK,
          type: 'boolean',
          isPrivate: true,
          element: <InStockField />
        }
      ];
    case ProductType.FORMULATIONS:
      return [
        {
          key: UxBeautyPrivateAttributes.VIDEO,
          type: 'string',
          isPrivate: false,
          element: <YoutubeVideoField />
        },
        {
          key: UxBeautyPrivateAttributes.TAGLINE,
          type: 'string',
          isPrivate: false,
          element: <TaglineField />
        },
        {
          key: UxBeautyPrivateAttributes.MARKET_TRENDS,
          type: 'multiSelect',
          isPrivate: true,
          element: <MarketTrendsField segment={SegmentTag.PERSONALCARE} />
        },
        {
          key: UxBeautyPrivateAttributes.MATERIAL_CODE,
          type: 'string',
          isPrivate: true,
          element: <MaterialCodeField />
        },

        {
          key: UxBeautyPrivateAttributes.PRODUCTION_SITE,
          type: 'singleSelect',
          isPrivate: true,
          element: <ProductionSiteField segment={SegmentTag.PERSONALCARE} />
        },
        {
          key: UxBeautyPrivateAttributes.POSITIONING,
          type: 'singleSelect',
          isPrivate: true,
          element: <PositioningField segment={SegmentTag.PERSONALCARE} />
        },
        {
          key: UxBeautyPrivateAttributes.CONSUMER_TESTS,
          type: 'boolean',
          isPrivate: true,
          element: <ConsumerTestsField />
        },
        {
          key: UxBeautyPrivateAttributes.SAMPLE_UNIT_PRICE,
          type: 'string',
          isPrivate: true,
          element: <SampleUnitPriceField />
        },
        {
          key: UxBeautyPrivateAttributes.SAMPLE_UNIT_SIZE,
          type: 'string',
          isPrivate: true,
          element: <SampleUnitSizeField />
        },
        {
          key: UxBeautyPrivateAttributes.STORAGE_CONDITIONS,
          type: 'string',
          isPrivate: true,
          element: <StorageConditionsField />
        },
        {
          key: UxBeautyPrivateAttributes.PH_STABILITY_RANGE,
          type: 'string',
          isPrivate: true,
          element: <PhStabilityRangeField />
        },
        {
          key: UxBeautyPrivateAttributes.VISCOSITY,
          type: 'string',
          isPrivate: true,
          element: <ViscosityField />
        },
        {
          key: UxBeautyPrivateAttributes.IN_STOCK,
          type: 'boolean',
          isPrivate: true,
          element: <InStockField />
        },
        {
          key: UxBeautyPrivateAttributes.BIOLOGICAL_TARGET,
          type: 'singleSelect',
          isPrivate: true,
          element: <BiologicalTargetField />
        }
      ];
    default:
      return [];
  }
};

export const ServicesTechnicalListAttributes = [
  {
    attribute: 'applications',
    segmented: true,
    creatable: false,
    grouped: false,
    filter: 'APPLICATIONS'
  }
];

export const FormulationsTechnicalListAttributes = [
  {
    attribute: 'claims',
    segmented: true,
    grouped: true,
    creatable: false,
    types: [
      { value: 'PERFORMANCE', filter: 'PERFORMANCE_CLAIMS' },
      { value: 'SUSTAINABILITY', filter: 'SUSTAINABILITY_CLAIMS' }
    ]
  },
  {
    attribute: 'properties',
    segmented: false,
    creatable: false,
    grouped: true,
    types: [
      { value: 'CHEMICAL', filter: 'CHEMICAL_PROPERTIES' },
      { value: 'PHYSICAL', filter: 'PHYSICAL_PROPERTIES' }
    ]
  },
  {
    attribute: 'organoleptic',
    creatable: false,
    segmented: false,
    grouped: true,
    types: [
      { value: 'COLOR', filter: 'COLORS' },
      { value: 'ODOR', filter: 'ODOR' },
      { value: 'APPEARANCES', filter: 'APPEARANCES' }
    ]
  },
  {
    attribute: 'certifications',
    segmented: true,
    creatable: false,
    grouped: false,
    defaultOptions: CertificationOptions
  },
  {
    attribute: 'applications',
    segmented: true,
    creatable: false,
    grouped: false,
    filter: 'APPLICATIONS'
  }
];
export const FoodIngredientsTechnicalListAttributes = [
  {
    attribute: 'claims',
    grouped: true,
    creatable: false,
    segmented: true,
    types: [
      { value: 'PERFORMANCE', filter: 'PERFORMANCE_CLAIMS' },
      { value: 'SUSTAINABILITY', filter: 'SUSTAINABILITY_CLAIMS' },
      { value: 'FREE_FROM_CLAIMS', filter: 'FREE_FROM_CLAIMS' }
    ]
  },
  {
    attribute: 'food-categories',
    segmented: true,
    creatable: false,
    grouped: false,
    filter: 'FOOD_CATEGORIES'
  },
  {
    attribute: 'active-components',
    segmented: true,
    creatable: false,
    grouped: false,
    filter: 'ACTIVE_COMPONENTS'
  },
  {
    attribute: 'functions',
    segmented: true,
    creatable: false,
    grouped: false,
    filter: 'FUNCTIONS'
  },
  // {
  //   attribute: 'free_form_claims',
  //   segmented: true,
  //   creatable: false,
  //   grouped: false,
  //   filter: 'FREE_FORM_CLAIMS'
  // },
  {
    attribute: 'applications',
    segmented: true,
    creatable: false,
    grouped: false,
    filter: 'APPLICATIONS'
  },
  {
    attribute: 'chemicalgroup',
    segmented: false,
    creatable: false,
    grouped: false,
    filter: 'CHEMICAL_GROUP'
  },
  {
    attribute: 'properties',
    segmented: false,
    creatable: false,
    grouped: true,
    types: [
      { value: 'CHEMICAL', filter: 'CHEMICAL_PROPERTIES' },
      { value: 'PHYSICAL', filter: 'PHYSICAL_PROPERTIES' }
    ]
  },
  {
    attribute: 'organoleptic',
    creatable: false,
    segmented: false,
    grouped: true,
    types: [
      { value: 'COLOR', filter: 'COLORS' },
      { value: 'APPEARANCES', filter: 'APPEARANCES' },
      { value: 'FLAVOR_FAMILY', filter: 'FLAVOR_FAMILY' },
      { value: 'FLAVOR', filter: 'FLAVOR' }
    ]
  },
  {
    attribute: 'origin',
    segmented: true,
    creatable: false,
    grouped: false,
    defaultOptions: OriginOptions
  },
  {
    attribute: 'origin-species',
    segmented: false,
    creatable: false,
    grouped: false,
    filter: 'ORIGINS'
  },
  {
    attribute: 'certifications',
    segmented: true,
    creatable: false,
    grouped: false,
    defaultOptions: CertificationOptions
  }
];
export const IngredientsTechnicalListAttributes = [
  {
    attribute: 'claims',
    grouped: true,
    creatable: false,
    segmented: true,
    types: [
      { value: 'PERFORMANCE', filter: 'PERFORMANCE_CLAIMS' },
      { value: 'SUSTAINABILITY', filter: 'SUSTAINABILITY_CLAIMS' }
    ]
  },
  {
    attribute: 'functions',
    segmented: true,
    creatable: false,
    grouped: false,
    filter: 'FUNCTIONS'
  },
  {
    attribute: 'applications',
    segmented: true,
    creatable: false,
    grouped: false,
    filter: 'APPLICATIONS'
  },
  {
    attribute: 'chemicalgroup',
    segmented: false,
    creatable: false,
    grouped: false,
    filter: 'CHEMICAL_GROUP'
  },
  {
    attribute: 'properties',
    segmented: false,
    creatable: false,
    grouped: true,
    types: [
      { value: 'CHEMICAL', filter: 'CHEMICAL_PROPERTIES' },
      { value: 'PHYSICAL', filter: 'PHYSICAL_PROPERTIES' }
    ]
  },
  {
    attribute: 'organoleptic',
    creatable: false,
    segmented: false,
    grouped: true,
    types: [
      { value: 'COLOR', filter: 'COLORS' },
      { value: 'ODOR', filter: 'ODOR' },
      { value: 'APPEARANCES', filter: 'APPEARANCES' }
    ]
  },
  {
    attribute: 'origin',
    segmented: true,
    creatable: false,
    grouped: false,
    defaultOptions: OriginOptions
  },
  {
    attribute: 'origin-species',
    segmented: false,
    creatable: false,
    grouped: false,
    filter: 'ORIGINS'
  },
  {
    attribute: 'certifications',
    segmented: true,
    creatable: false,
    grouped: false,
    defaultOptions: CertificationOptions
  }
];

export const ComplianceAttributes = [
  {
    attribute: 'hazardstatements',
    segmented: false,
    creatable: false,
    grouped: false,
    filter: 'HAZARD_STATEMENTS'
  },
  {
    attribute: 'hazardclassification',
    segmented: false,
    creatable: false,
    grouped: false,
    filter: 'GHS_CLASSIFICATION'
  }
];

export const RenderCategories = {
  INGREDIENTS: {
    technical: IngredientsTechnicalListAttributes,
    render: ['data', 'technical', 'compliance'],
    parse: ['technical', 'compliance']
  },
  FORMULATIONS: {
    technical: FormulationsTechnicalListAttributes,
    render: ['data', 'technical', 'formulation', 'procedures'],
    parse: ['technical']
  },
  SERVICES: {
    render: ['data'],
    parse: ['technical']
  }
};
